import { useTranslation } from "react-i18next";
import React from "react";
import SETTINGS_CONTEXT from "src/context/internal/settings";
import { TableCell, TableRow } from "src/components/base/table";
import { Strong, Text } from "./base/text";
import { Badge } from "src/components/base/badges/base";
import {
    Dropdown,
    DropdownButton,
    DropdownDivider,
    DropdownItem,
    DropdownLabel,
    DropdownMenu,
} from "src/components/base/dropdown";
import { toast } from "react-toastify";
import { ChevronDownIcon, ClipboardDocumentIcon } from "@heroicons/react/20/solid";
import { FullDevice } from "src/api/generated/frontend_internal_v1";
import { TagBadge } from "src/components/base/badges/tag";

/**
 * The properties for {@link DeviceRowCustomer}
 */
export type DeviceRowPropsCustomer = {
    /** The device that should be rendered */
    device: FullDevice;
};

/**
 * A single row in the device overview table
 */
export function DeviceRowCustomer(props: DeviceRowPropsCustomer) {
    const [t] = useTranslation("admin-device-overview");
    const [tg] = useTranslation();

    const { device } = props;

    const settingsContext = React.useContext(SETTINGS_CONTEXT);

    return (
        <TableRow href={"/c/devices/$deviceId/general"} params={{ deviceId: device.uuid }}>
            <TableCell>
                <div className={"flex flex-col gap-1"}>
                    <Strong>{device.dnsname}</Strong>
                    {device.user ? (
                        <Text>device.user.display_name</Text>
                    ) : (
                        <div className={"flex flex-wrap gap-1"}>
                            {device.tags?.map((tag) => (
                                <TagBadge key={tag.uuid} tag={tag} />
                            ))}
                        </div>
                    )}
                </div>
            </TableCell>
            <TableCell>
                {device.online ? (
                    <div className={"flex items-center gap-2"}>
                        <div className={"size-2 animate-pulse rounded-sm bg-green-500"} />
                        <Text className={"max-lg:hidden"}>{t("label.online")}</Text>
                    </div>
                ) : device.last_seen ? (
                    new Date(device.last_seen).toLocaleString("de-de")
                ) : (
                    "?"
                )}
            </TableCell>
            <TableCell>
                <div className={"flex items-center gap-1"}>
                    <span>{device.ipv4}</span>
                    <Dropdown>
                        <DropdownButton plain={true}>
                            <ChevronDownIcon />
                        </DropdownButton>
                        <DropdownMenu anchor={"bottom end"}>
                            {[
                                device.ipv4,
                                device.ipv6,
                                device.dnsname,
                                ...settingsContext.settings.headscale_domains.map(
                                    (domain) => `${device.dnsname}.${domain}`,
                                ),
                            ].map(
                                (address) =>
                                    address && (
                                        <DropdownItem
                                            key={address}
                                            onClick={async () => {
                                                await navigator.clipboard.writeText(address);
                                                toast.success(tg("toast.copied-to-clipboard"));
                                            }}
                                        >
                                            <ClipboardDocumentIcon />
                                            <DropdownLabel>{address}</DropdownLabel>
                                        </DropdownItem>
                                    ),
                            )}
                            {device.extra_records.length > 0 && (
                                <>
                                    <DropdownDivider />
                                    {device.extra_records.map((record) => (
                                        <DropdownItem
                                            key={record.uuid}
                                            onClick={async () => {
                                                await navigator.clipboard.writeText(record.from_dns);
                                                toast.success(tg("toast.copied-to-clipboard"));
                                            }}
                                        >
                                            <ClipboardDocumentIcon />
                                            <DropdownLabel className={"flex w-full justify-between gap-2"}>
                                                <span className={"w-36 truncate"}>{record.from_dns}</span>
                                                <Badge color={"blue"}>{t("label.extra-dns")}</Badge>
                                            </DropdownLabel>
                                        </DropdownItem>
                                    ))}
                                </>
                            )}
                        </DropdownMenu>
                    </Dropdown>
                </div>
            </TableCell>
            <TableCell className={"max-lg:hidden"}>
                {device.expiry ? new Date(device.expiry).toLocaleDateString("de-de") : "-"}
            </TableCell>
            <TableCell className={"max-lg:hidden"}>{new Date(device.created_at).toLocaleDateString("de-de")}</TableCell>
        </TableRow>
    );
}
